import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { DateRange, Calendar as SingleCalendar } from "react-date-range";
import { fr } from "date-fns/locale";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import {
  TbCalendar, TbUser, TbBed, TbCheck, TbArrowLeft, TbArrowRight,
  TbAlertCircle, TbUsers, TbMinus, TbPlus, TbCurrencyEuro, TbInfoCircle
} from "react-icons/tb";

const API = "https://api.komorotel.com/api";

const fmtInput = (d) => new Date(d).toLocaleDateString("sv-SE");
const fmtDisplay = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

/* ═══════════════════════════════════════════════════════════
   🆕 PÉRIODES

   Un tarif forfaitaire ne se réserve pas entre deux dates libres : on choisit
   une arrivée et un nombre de semaines ou de mois, et la sortie se calcule.
   La règle vaut aussi en back-office, sinon l'hébergeur produirait des
   réservations que la grille tarifaire ne sait pas facturer.

   Le backend attend alors `periods` et ignore `checkOutDate`.
   ═══════════════════════════════════════════════════════════ */
const UNIT = {
  short_stay: { label: "nuit",    nights: 1  },
  weekly:     { label: "semaine", nights: 7  },
  monthly:    { label: "mois",    nights: 30 },
};

const normalizeStayType = (t) =>
  t === "long_stay" ? "monthly" : (["short_stay", "weekly", "monthly"].includes(t) ? t : "short_stay");

const unitOf = (stayType) => UNIT[normalizeStayType(stayType)];
const isPeriodPlan = (stayType) => normalizeStayType(stayType) !== "short_stay";

const unitPlural = (stayType, n) => {
  const l = unitOf(stayType).label;
  if (l === "mois") return l;
  return n > 1 ? `${l}s` : l;
};

export default function BookReservation() {
  const { hotelId } = useParams();

  const [step, setStep] = useState(1);
  const [rooms, setRooms] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    roomId: "",
    ratePlanId: "",
    checkIn: fmtInput(new Date()),
    checkOut: fmtInput(addDays(new Date(), 1)),
    adults: 1,
    children: 0,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  // 🆕 PÉRIODES — nombre de périodes, utilisé uniquement sur un plan forfaitaire
  const [periods, setPeriods] = useState(1);

  const [showCal, setShowCal] = useState(false);
  const [price, setPrice] = useState(null);
  const [availability, setAvailability] = useState(null);

  const [dateRange, setDateRange] = useState([{
    startDate: new Date(),
    endDate: addDays(new Date(), 1),
    key: "selection",
  }]);

  // ── Fetch rooms + rate plans ──
  useEffect(() => {
    axios.get(`${API}/rooms/hotel/${hotelId}`)
      .then(r => setRooms(r.data))
      .catch(console.error);

    axios.get(`${API}/rateplans/hotel/${hotelId}`, { withCredentials: true })
      .then(r => setRatePlans(r.data))
      .catch(console.error);
  }, [hotelId]);

  // ── Plan sélectionné et unité ──
  const selectedPlan = useMemo(
    () => ratePlans.find(p => p._id?.toString() === form.ratePlanId?.toString()) || null,
    [ratePlans, form.ratePlanId]
  );
  const period = selectedPlan ? isPeriodPlan(selectedPlan.stayType) : false;
  const unit   = selectedPlan ? unitOf(selectedPlan.stayType) : UNIT.short_stay;

  // 🆕 PÉRIODES — sur un forfait, la sortie est calculée, jamais saisie.
  const effectiveCheckOut = period
    ? fmtInput(addDays(new Date(form.checkIn), periods * unit.nights))
    : form.checkOut;

  const nights = period
    ? periods * unit.nights
    : Math.max(1, Math.ceil((new Date(form.checkOut) - new Date(form.checkIn)) / 86400000));

  const selectedRoom = rooms.find(r => r._id === form.roomId);

  // 🆕 ASSOCIATION — seuls les tarifs rattachés à cette chambre
  const availablePlans = useMemo(() => {
    if (!form.roomId) return [];
    return ratePlans.filter(plan =>
      (plan.roomIds || []).map(id => id?.toString?.() ?? id).includes(form.roomId.toString())
    );
  }, [ratePlans, form.roomId]);

  // 🆕 PÉRIODES — changer de chambre remet le tarif à zéro, donc l'unité aussi
  const handleRoomChange = (roomId) => {
    setForm(f => ({ ...f, roomId, ratePlanId: "" }));
    setPeriods(1);
    setPrice(null);
  };

  const handlePlanChange = (planId) => {
    setForm(f => ({ ...f, ratePlanId: planId }));
    setPeriods(1);
    setPrice(null);
  };

  // ── Calcul du prix ──
  useEffect(() => {
    if (!form.roomId || !form.ratePlanId || !form.checkIn) {
      setPrice(null);
      return;
    }
    if (!period && !form.checkOut) {
      setPrice(null);
      return;
    }

    // 🆕 PÉRIODES — le calcul prend stayMode + periods sur un forfait
    const params = period
      ? {
          roomId: form.roomId,
          ratePlanId: form.ratePlanId,
          checkIn: form.checkIn,
          stayMode: normalizeStayType(selectedPlan.stayType),
          periods,
          adults: form.adults,
          children: form.children,
        }
      : {
          roomId: form.roomId,
          ratePlanId: form.ratePlanId,
          checkIn: form.checkIn,
          checkOut: form.checkOut,
          adults: form.adults,
          children: form.children,
        };

    axios.get(`${API}/rateprices/calculate`, { params })
      .then(r => { setPrice(r.data); setError(null); })
      .catch(err => {
        setPrice(null);
        setError(err.response?.data?.error || "Impossible de calculer le prix pour ce tarif.");
      });
  }, [form.roomId, form.ratePlanId, form.checkIn, form.checkOut, form.adults, form.children, periods, period, selectedPlan]);

  // ── Vérif disponibilité ──
  useEffect(() => {
    if (!form.roomId || !form.checkIn || !effectiveCheckOut) return;

    axios.get(`${API}/availability/${form.roomId}`, {
      params: { startDate: form.checkIn, endDate: effectiveCheckOut },
      withCredentials: true
    })
      .then(r => {
        const days = r.data || [];
        const unavailable = days.filter(d =>
          d.status === "closed" || (d.totalRooms - d.bookedRooms) <= 0
        );
        setAvailability({ ok: unavailable.length === 0, unavailable });
      })
      .catch(() => setAvailability(null));
  }, [form.roomId, form.checkIn, effectiveCheckOut]);

  // ── Submit ──
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/reservation/admin`, {
        roomId: form.roomId,
        ratePlanId: form.ratePlanId,
        checkInDate: form.checkIn,
        // 🆕 PÉRIODES — sur un forfait c'est `periods` qui décrit le séjour.
        // Envoyer checkOutDate ferait échouer resolvePeriodPrice.
        ...(period ? { periods } : { checkOutDate: form.checkOut }),
        guests: { adults: form.adults, children: form.children },
        guest: {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email || undefined,
          phone: form.phone,
        },
      }, { withCredentials: true });

      setSuccess(data);
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Erreur lors de la réservation");
    } finally {
      setLoading(false);
    }
  };

  const canProceed = {
    1: form.roomId && form.ratePlanId && form.checkIn && (period ? periods >= 1 : form.checkOut) && availability?.ok,
    2: form.firstName && form.lastName && form.phone,
    3: true,
  };

  const steps = [
    { n: 1, label: "Séjour", icon: TbBed },
    { n: 2, label: "Client", icon: TbUser },
    { n: 3, label: "Confirmation", icon: TbCheck },
  ];

  // 🆕 PÉRIODES — prix du forfait, hors taxe de séjour
  const baseTotal = price
    ? (price.taxConfig?.enabled && !price.taxConfig?.included
        ? Math.round((price.total - (price.taxTotal || 0)) * 100) / 100
        : price.total)
    : null;
  const perPeriod = period && baseTotal != null
    ? Math.round((baseTotal / periods) * 100) / 100
    : null;

  // ── Succès ──
  if (step === 4 && success) {
    return (
      <div className="w-full min-h-screen bg-[#F8FAFC] p-6 flex items-center justify-center">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
            <TbCheck className="text-emerald-600 text-2xl" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Réservation créée</h2>
          <p className="text-sm text-gray-400 mb-6">
            {success.guest?.firstName} {success.guest?.lastName}
          </p>

          <div className="bg-gray-50 rounded-xl p-4 text-left flex flex-col gap-2 mb-6">
            {[
              { label: "Référence", value: success.reservationId },
              { label: "Chambre",   value: selectedRoom?.title },
              { label: "Dates",     value: `${fmtDisplay(success.checkInDate)} → ${fmtDisplay(success.checkOutDate)}` },
              // 🆕 PÉRIODES — durée dans l'unité vendue
              { label: "Durée",     value: success.periods
                  ? `${success.periods} ${unitPlural(selectedPlan?.stayType, success.periods)} · ${success.totalNights} nuits`
                  : `${success.totalNights} nuit${success.totalNights > 1 ? "s" : ""}` },
              { label: "Total",     value: `${success.totalPrice?.toLocaleString("fr-FR")} €` },
            ].map((row, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-gray-400">{row.label}</span>
                <span className="font-semibold text-gray-800">{row.value}</span>
              </div>
            ))}
          </div>

          <button
            onClick={() => {
              setStep(1);
              setSuccess(null);
              setPeriods(1);
              setForm(f => ({ ...f, roomId: "", ratePlanId: "", firstName: "", lastName: "", email: "", phone: "" }));
            }}
            className="w-full py-2.5 rounded-xl bg-[var(--k-green)] text-white text-sm font-semibold hover:opacity-90 transition"
          >
            Nouvelle réservation
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-[#F8FAFC] p-6 flex flex-col gap-6">

      {/* HEADER */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Nouvelle réservation</h1>
        <p className="text-sm text-gray-400 mt-1">Saisie manuelle — sans commission Komorotel</p>
      </div>

      {/* STEPPER */}
      <div className="flex items-center gap-2">
        {steps.map((s, i) => {
          const Icon = s.icon;
          const active = step === s.n;
          const done = step > s.n;
          return (
            <div key={s.n} className="flex items-center gap-2 flex-1">
              <div className={`flex items-center gap-2 px-3 py-2 rounded-xl transition ${
                active ? "bg-gray-900 text-white" : done ? "bg-emerald-50 text-emerald-700" : "bg-white border border-gray-100 text-gray-400"
              }`}>
                <Icon className="text-base shrink-0" />
                <span className="text-sm font-semibold whitespace-nowrap">{s.label}</span>
              </div>
              {i < steps.length - 1 && <div className={`flex-1 h-px ${done ? "bg-emerald-200" : "bg-gray-200"}`} />}
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 max-w-2xl">

        {/* ═══ STEP 1 : SÉJOUR ═══ */}
        {step === 1 && (
          <div className="flex flex-col gap-5">

            {/* Chambre */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">Chambre</label>
              <div className="flex flex-col gap-2">
                {rooms.map(room => (
                  <button
                    key={room._id}
                    onClick={() => handleRoomChange(room._id)}
                    className={`text-left px-4 py-3 rounded-xl border transition ${
                      form.roomId === room._id
                        ? "border-[var(--k-green)] bg-[rgb(var(--k-green-rgb)/0.05)]"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <p className="text-sm font-semibold text-gray-900">{room.title}</p>
                    <p className="text-xs text-gray-400">{room.maxPeople} personnes max</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Tarif */}
            {form.roomId && (
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">Tarif</label>
                {availablePlans.length === 0 ? (
                  <div className="px-4 py-3 bg-amber-50 border border-amber-100 rounded-xl">
                    <p className="text-xs text-amber-700">
                      Aucun tarif associé à cette chambre. Configurez-en un dans la page Tarifs.
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {availablePlans.map(plan => {
                      const u = unitOf(plan.stayType);
                      const p = isPeriodPlan(plan.stayType);
                      return (
                        <button
                          key={plan._id}
                          onClick={() => handlePlanChange(plan._id)}
                          className={`text-left px-4 py-3 rounded-xl border transition flex items-center justify-between gap-3 ${
                            form.ratePlanId === plan._id
                              ? "border-[var(--k-green)] bg-[rgb(var(--k-green-rgb)/0.05)]"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{plan.name}</p>
                            {plan.description && (
                              <p className="text-xs text-gray-400 truncate">{plan.description}</p>
                            )}
                          </div>
                          {/* 🆕 PÉRIODES — l'unité de vente, dès la sélection */}
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${
                            p ? "bg-[rgb(var(--k-green-rgb)/0.1)] text-[var(--k-green)]" : "bg-gray-100 text-gray-500"
                          }`}>
                            / {u.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* 🆕 PÉRIODES — durée en semaines ou en mois */}
            {period && (
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
                  Durée du séjour
                </label>
                <div className="flex items-center justify-between px-4 py-3 border border-gray-200 rounded-xl">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {periods} {unitPlural(selectedPlan.stayType, periods)}
                    </p>
                    <p className="text-xs text-gray-400">{nights} nuits au total</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setPeriods(p => Math.max(1, p - 1))}
                      disabled={periods <= 1}
                      className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-gray-400 transition disabled:opacity-30"
                    >
                      <TbMinus />
                    </button>
                    <span className="text-base font-bold w-6 text-center">{periods}</span>
                    <button
                      onClick={() => setPeriods(p => p + 1)}
                      className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-gray-400 transition"
                    >
                      <TbPlus />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Dates */}
            <div className="relative">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
                {period ? "Date d'arrivée" : "Dates"}
              </label>
              <button
                onClick={() => setShowCal(!showCal)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl flex items-center gap-3 hover:border-gray-300 transition"
              >
                <TbCalendar className="text-gray-400 shrink-0" />
                <span className="text-sm text-gray-800">
                  {/* 🆕 PÉRIODES — la sortie est annoncée comme calculée */}
                  {fmtDisplay(form.checkIn)} → {fmtDisplay(effectiveCheckOut)}
                  {period && <span className="text-gray-400"> (calculé)</span>}
                </span>
                <span className="ml-auto text-xs text-gray-400 shrink-0">{nights} nuit{nights > 1 ? "s" : ""}</span>
              </button>

              {showCal && (
                <div className="absolute z-30 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
                  {/* 🆕 PÉRIODES — une seule date à choisir sur un forfait */}
                  {period ? (
                    <SingleCalendar
                      date={new Date(form.checkIn)}
                      onChange={(d) => setForm(f => ({ ...f, checkIn: fmtInput(d) }))}
                      locale={fr}
                      color="var(--k-green)"
                    />
                  ) : (
                    <DateRange
                      ranges={dateRange}
                      onChange={(item) => {
                        setDateRange([item.selection]);
                        setForm(f => ({
                          ...f,
                          checkIn: fmtInput(item.selection.startDate),
                          checkOut: fmtInput(item.selection.endDate),
                        }));
                      }}
                      locale={fr}
                      months={1}
                      minDate={new Date()}
                      rangeColors={["var(--k-green)"]}
                    />
                  )}
                  <div className="px-4 py-3 border-t border-gray-100 text-right">
                    <button
                      onClick={() => setShowCal(false)}
                      className="px-4 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-semibold"
                    >
                      Valider
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Voyageurs */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">Voyageurs</label>
              <div className="flex flex-col gap-2">
                {[
                  { key: "adults",   label: "Adultes",  hint: "13 ans et plus",  min: 1 },
                  { key: "children", label: "Enfants",  hint: "Moins de 13 ans", min: 0 },
                ].map(g => (
                  <div key={g.key} className="flex items-center justify-between px-4 py-3 border border-gray-200 rounded-xl">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{g.label}</p>
                      <p className="text-xs text-gray-400">{g.hint}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setForm(f => ({ ...f, [g.key]: Math.max(g.min, f[g.key] - 1) }))}
                        disabled={form[g.key] <= g.min}
                        className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-gray-400 transition disabled:opacity-30"
                      >
                        <TbMinus />
                      </button>
                      <span className="text-base font-bold w-6 text-center">{form[g.key]}</span>
                      <button
                        onClick={() => setForm(f => ({ ...f, [g.key]: f[g.key] + 1 }))}
                        className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-gray-400 transition"
                      >
                        <TbPlus />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {selectedRoom && (form.adults + form.children) > selectedRoom.maxPeople && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1.5">
                  <TbAlertCircle /> Capacité de la chambre dépassée ({selectedRoom.maxPeople} max)
                </p>
              )}
            </div>

            {/* Disponibilité */}
            {availability && !availability.ok && (
              <div className="px-4 py-3 bg-red-50 border border-red-100 rounded-xl">
                <p className="text-sm text-red-700 font-semibold mb-1">Chambre indisponible</p>
                <p className="text-xs text-red-600">
                  {availability.unavailable.length} jour(s) fermé(s) ou complet(s) sur la période sélectionnée.
                </p>
              </div>
            )}

            {/* Prix */}
            {price && (
              <div className="px-4 py-4 bg-gray-50 rounded-xl">
                {/* 🆕 PÉRIODES — la ligne suit l'unité réellement facturée */}
                <div className="flex justify-between text-sm text-gray-500 mb-1.5">
                  <span>
                    {period
                      ? `${perPeriod?.toLocaleString("fr-FR")} € × ${periods} ${unitPlural(selectedPlan.stayType, periods)}`
                      : `${price.pricePerNight} € × ${nights} nuit${nights > 1 ? "s" : ""}`}
                  </span>
                  <span>{baseTotal?.toLocaleString("fr-FR")} €</span>
                </div>
                {price.taxConfig?.enabled && !price.taxConfig?.included && (
                  <div className="flex justify-between text-xs text-gray-400 mb-1.5">
                    <span>Taxe de séjour</span>
                    <span>+{price.taxTotal?.toFixed(2)} €</span>
                  </div>
                )}
                <div className="flex justify-between items-baseline pt-2 border-t border-gray-200">
                  <span className="text-sm font-semibold text-gray-700">Total</span>
                  <div className="text-right">
                    <p className="text-xl font-bold text-gray-900">{price.total?.toLocaleString("fr-FR")} €</p>
                    {price.totalKMF && (
                      <p className="text-xs text-gray-400">{price.totalKMF?.toLocaleString("fr-FR")} KMF</p>
                    )}
                  </div>
                </div>
                {period && price.pricePerNight && (
                  <p className="text-[11px] text-gray-400 mt-2">
                    soit ≈ {price.pricePerNight} € par nuit
                  </p>
                )}
              </div>
            )}

            {/* 🆕 PÉRIODES — pas de versement en back-office.
                L'hébergeur encaisse lui-même, hors plateforme : la réservation
                est confirmée immédiatement, contrairement au site public. */}
            {period && (
              <div className="px-4 py-3 bg-blue-50 border border-blue-100 rounded-xl flex items-start gap-2.5">
                <TbInfoCircle className="text-blue-500 shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700 leading-relaxed">
                  Saisie manuelle : la réservation sera confirmée immédiatement, sans attente de
                  règlement et sans commission Komorotel. Vous encaissez directement.
                </p>
              </div>
            )}

            {error && (
              <div className="px-4 py-3 bg-red-50 border border-red-100 rounded-xl flex items-start gap-2.5">
                <TbAlertCircle className="text-red-500 shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>
        )}

        {/* ═══ STEP 2 : CLIENT ═══ */}
        {step === 2 && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: "lastName",  label: "Nom *",     placeholder: "Dupont" },
                { key: "firstName", label: "Prénom *",  placeholder: "Marie" },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5">{f.label}</label>
                  <input
                    type="text"
                    value={form[f.key]}
                    onChange={(e) => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                    placeholder={f.placeholder}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-gray-400 transition"
                  />
                </div>
              ))}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Téléphone *</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+269 ..."
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-gray-400 transition"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                Email <span className="font-normal text-gray-400">(facultatif)</span>
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="marie@exemple.com"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-gray-400 transition"
              />
              <p className="text-[11px] text-gray-400 mt-1.5">
                Sans email, aucune confirmation ne sera envoyée au client.
              </p>
            </div>
          </div>
        )}

        {/* ═══ STEP 3 : CONFIRMATION ═══ */}
        {step === 3 && (
          <div className="flex flex-col gap-4">
            <div className="bg-gray-50 rounded-xl p-4 flex flex-col gap-2.5">
              {[
                { label: "Client",    value: `${form.firstName} ${form.lastName}` },
                { label: "Téléphone", value: form.phone },
                ...(form.email ? [{ label: "Email", value: form.email }] : []),
                { label: "Chambre",   value: selectedRoom?.title },
                { label: "Tarif",     value: selectedPlan?.name },
                { label: "Arrivée",   value: fmtDisplay(form.checkIn) },
                { label: period ? "Départ (calculé)" : "Départ", value: fmtDisplay(effectiveCheckOut) },
                // 🆕 PÉRIODES — durée dans l'unité vendue
                { label: "Durée",     value: period
                    ? `${periods} ${unitPlural(selectedPlan.stayType, periods)} · ${nights} nuits`
                    : `${nights} nuit${nights > 1 ? "s" : ""}` },
                { label: "Voyageurs", value: `${form.adults} adulte${form.adults > 1 ? "s" : ""}${form.children > 0 ? ` · ${form.children} enfant${form.children > 1 ? "s" : ""}` : ""}` },
              ].map((row, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-gray-400">{row.label}</span>
                  <span className="font-medium text-gray-800 text-right max-w-[60%] truncate">{row.value}</span>
                </div>
              ))}
            </div>

            {price && (
              <div className="flex items-baseline justify-between px-4 py-4 bg-[rgb(var(--k-green-rgb)/0.05)] border border-[rgb(var(--k-green-rgb)/0.15)] rounded-xl">
                <span className="text-sm font-semibold text-gray-700">Total à percevoir</span>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">{price.total?.toLocaleString("fr-FR")} €</p>
                  {price.totalKMF && (
                    <p className="text-xs text-gray-400">{price.totalKMF?.toLocaleString("fr-FR")} KMF</p>
                  )}
                </div>
              </div>
            )}

            <div className="px-4 py-3 bg-blue-50 border border-blue-100 rounded-xl flex items-start gap-2.5">
              <TbCurrencyEuro className="text-blue-500 shrink-0 mt-0.5" />
              <p className="text-xs text-blue-700 leading-relaxed">
                Réservation en saisie manuelle : aucune commission Komorotel n'est prélevée.
                Vous encaissez la totalité.
              </p>
            </div>

            {error && (
              <div className="px-4 py-3 bg-red-50 border border-red-100 rounded-xl flex items-start gap-2.5">
                <TbAlertCircle className="text-red-500 shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>
        )}

        {/* NAVIGATION */}
        <div className="flex gap-2 mt-6 pt-5 border-t border-gray-100">
          {step > 1 && (
            <button
              onClick={() => { setStep(step - 1); setError(null); }}
              className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 transition flex items-center gap-1.5"
            >
              <TbArrowLeft /> Retour
            </button>
          )}
          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed[step]}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 transition disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
            >
              Continuer <TbArrowRight />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-[var(--k-green)] text-white hover:opacity-90 transition disabled:opacity-40"
            >
              {loading ? "Création…" : "Créer la réservation"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}